// Chord symbols as they appear in Guitarparty songs ("Am", "G/B", "F#m7b5",
// "Hm", "Csus4") parsed into pitch classes, for the chord listener and the
// bass line generator.

const NOTE_PCS = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11, H: 11 };

// Semitones above the root. Looked up by suffix after normalisation; unknown
// suffixes fall back to the longest known prefix, then to a plain triad.
const QUALITIES = {
    '': [0, 4, 7],
    'm': [0, 3, 7],
    '5': [0, 7],
    '6': [0, 4, 7, 9],
    'm6': [0, 3, 7, 9],
    '69': [0, 4, 7, 9, 2],
    '7': [0, 4, 7, 10],
    'm7': [0, 3, 7, 10],
    'maj7': [0, 4, 7, 11],
    'mmaj7': [0, 3, 7, 11],
    '9': [0, 4, 7, 10, 2],
    'm9': [0, 3, 7, 10, 2],
    'maj9': [0, 4, 7, 11, 2],
    '11': [0, 7, 10, 2, 5],
    'm11': [0, 3, 7, 10, 5],
    '13': [0, 4, 7, 10, 9],
    'add9': [0, 4, 7, 2],
    'madd9': [0, 3, 7, 2],
    'add4': [0, 4, 5, 7],
    'madd4': [0, 3, 5, 7],
    'madd6': [0, 3, 7, 9],
    'sus4': [0, 5, 7],
    'sus2': [0, 2, 7],
    'msus2': [0, 2, 7],
    'msus4': [0, 5, 7],
    '7sus4': [0, 5, 7, 10],
    '7sus2': [0, 2, 7, 10],
    'm7sus4': [0, 5, 7, 10],
    '9sus4': [0, 5, 7, 10, 2],
    'maj7sus2': [0, 2, 7, 11],
    '6sus2': [0, 2, 7, 9],
    'dim': [0, 3, 6],
    'dim7': [0, 3, 6, 9],
    'm7b5': [0, 3, 6, 10],
    '7b5': [0, 4, 6, 10],
    'aug': [0, 4, 8],
    'maug': [0, 3, 8],
    '7aug': [0, 4, 8, 10],
    '7b9': [0, 4, 7, 10, 1],
    '7#9': [0, 4, 7, 10, 3],
    'm7b9': [0, 3, 7, 10, 1],
    '7alt': [0, 4, 10, 1, 3],
};

function normaliseSuffix(suffix) {
    return suffix
        .replace(/[()]/g, '')
        .replace(/^(Maj|M)(?=7|9|$)/, 'maj')
        .replace(/^m(Maj|M|maj|aug)7/, 'mmaj7')
        .replace(/7M$/, 'maj7')
        .replace(/^min/, 'm')
        .replace(/^-/, 'm')
        .replace(/^maj$/, '')
        .replace(/#5|\+5|aug5|\+/g, 'aug')
        .replace(/augaug/g, 'aug')
        .replace(/^(7?)augaug?/, '$1aug')
        .replace(/^aug7/, '7aug')
        .replace(/^m#$/, 'maug')
        .replace(/add2$/, 'add9')
        .replace(/add11$/, 'add4')
        .replace(/sus$/, 'sus4')
        .replace(/^(m?)2$/, '$1sus2')
        .replace(/^(m?)4$/, '$1sus4')
        .replace(/^79$/, '9')
        .replace(/^7\+9$/, '7#9')
        .replace(/alt$/, '7alt')
        .replace(/^(m?)77alt$/, '$17alt');
}

function lookupQuality(suffix) {
    for (let end = suffix.length; end >= 0; end--) {
        const intervals = QUALITIES[suffix.slice(0, end)];
        if (intervals) {
            return intervals;
        }
    }
    return suffix.startsWith('m') && !suffix.startsWith('maj') ? QUALITIES.m : QUALITIES[''];
}

export function notePc(name) {
    const match = /^([A-Ha-h])(#|b)?/.exec(name);
    if (!match) {
        return null;
    }
    let pc = NOTE_PCS[match[1].toUpperCase()];
    if (match[2] === '#') pc += 1;
    if (match[2] === 'b') pc -= 1;
    return (pc + 12) % 12;
}

// Returns { name, root, bass, intervals, pcs, third, fifth } or null when the
// symbol isn't a chord (e.g. "2x" repeat markers).
export function parseChord(name) {
    if (!name) {
        return null;
    }
    // Anything after a slash is a bass note ("G/B") or, now and then, an
    // extension written as one ("E7/9"), which is ignored.
    const match = /^([A-Ha-h](?:#|b)?)([^/]*)(?:\/(.*))?$/.exec(name.trim());
    if (!match) {
        return null;
    }
    const root = notePc(match[1]);
    const intervals = lookupQuality(normaliseSuffix(match[2]));
    const slashBass = match[3] ? notePc(match[3]) : null;
    const bass = slashBass === null ? root : slashBass;
    const pcs = intervals.map(i => (root + i) % 12);
    if (!pcs.includes(bass)) {
        pcs.push(bass);
    }
    const third = intervals.find(i => i === 3 || i === 4) ?? intervals.find(i => i === 5 || i === 2) ?? 7;
    const fifth = intervals.find(i => i === 7 || i === 6 || i === 8) ?? 7;
    return { name, root, bass, intervals, pcs, third, fifth };
}

// A parsed chord as it sounds `semitones` higher, e.g. its shape played with
// a capo on that fret. The name stays as written.
export function transposeChord(chord, semitones) {
    if (!chord || !semitones) {
        return chord;
    }
    const shift = pc => ((pc + semitones) % 12 + 12) % 12;
    return { ...chord, root: shift(chord.root), bass: shift(chord.bass), pcs: chord.pcs.map(shift) };
}

// Harmonic content of a plucked string: partials 1-6 land on the root, the
// octave, the fifth (3rd partial) and the major third (5th partial). Weighting
// templates this way matches what the microphone actually hears much better
// than a plain on/off chord template does.
const PARTIAL_OFFSETS = [0, 0, 7, 0, 4, 7];
const PARTIAL_DECAY = 0.6;

export function chordTemplate(chord) {
    const template = new Float32Array(12);
    const add = (pc, weight) => {
        PARTIAL_OFFSETS.forEach((offset, h) => {
            template[(pc + offset) % 12] += weight * Math.pow(PARTIAL_DECAY, h);
        });
    };
    chord.pcs.forEach(pc => add(pc, pc === chord.root ? 1.2 : 1));
    if (chord.bass !== chord.root) {
        add(chord.bass, 0.6);
    }
    let norm = 0;
    template.forEach(v => { norm += v * v; });
    norm = Math.sqrt(norm);
    return template.map(v => v / norm);
}

// Chord symbols in reading order from the server-rendered song body.
export function chordSequence(bodyChordified) {
    const names = [];
    const re = /data-chord="([^"]+)"/g;
    let match;
    while ((match = re.exec(bodyChordified || '')) !== null) {
        names.push(match[1]);
    }
    return names;
}

export const PC_NAMES = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
